<template>
	<Flyout
		title="Manage Clients"
		subHeading="Changes to clients are applied when Update clients
					is clicked and will not require a Publish. Clients
					assigned to this form cannot be unassigned, but can be
					disabled."
		parentTag="form"
		:display="display"
		@show="closeManageCustomersEditFlyout"
		:novalidate="'novalidate'"
	>
		<template #flyout-content
			><div class="manage-customers">
				<div class="manage-customers__assigned">
					<span class="manage-customers__assigned-title" v-if="isFormEmptyObject">
						{{
							form.status !== 'U'
								? 'Assigned Clients'
								: 'Pre-Assign Clients'
						}}
						({{ assigned.length }})
					</span>
					<ul class="manage-customers__list" v-if="assigned.length">
						<li
							class="manage-customers__list-item"
							v-for="customer in assigned"
							:key="customer.value"
						>
							<span class="manage-customers__text"
								>({{ customer.description }})
								{{ customer.text }}</span
							>
							<RadioButtonGroup
								:items="assignedOptionItems"
								v-model="customer.enabled"
							/>
						</li>
					</ul>
				</div>

				<div class="manage-customers__content">
					<CustomerAssignation
						v-model="customers"
						:isFlyout="true"
						:assignedCustomersIds="assignedCustomersIds"
						@change="updateCustomers"
					/>
				</div></div
		></template>

		<template #flyout-footer>
			<ButtonGroup
				:items="[
					{
						btnClick: saveAction,
						className: 'btn--primary',
						labelText: 'Update Clients',
						disabled: disableUpdateCustomersButton
					},
					{
						btnClick: cancelAction,
						className: 'btn btn--link',
						labelText: 'Cancel'
					}
				]"
			/>
		</template>
	</Flyout>
</template>

<script>
import Flyout from '@/components/shells/flyout/flyout';
import ButtonGroup from '@/components/forms/button/button-group';
import RadioButtonGroup from '@/components/forms/selection-control/radiobutton/radiobutton-group';
import CustomerAssignation from '@/sections/forms/views/customer-assignation.vue';
import CustomerOptionsMixin from '@/mixins/customers/customer-options';
import { CUSTOMER_OPTIONS } from '@/sections/options/options-mutation-type';
import { filterAndSortArray } from '@/utils/function';
import { mapActions } from 'vuex';
import _ from 'lodash';

export default {
	name: 'ManageCustomersEditFlyout',
	mixins: [CustomerOptionsMixin],
	components: {
		Flyout,
		ButtonGroup,
		RadioButtonGroup,
		CustomerAssignation
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		display: {
			type: Boolean,
			default: false
		},
		validateBeforeClose: {
			type: Boolean,
			default: false
		},
		form: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			assignedOptionItems: [
				{
					labelText: 'Enable',
					value: true
				},
				{
					labelText: 'Disable',
					value: false
				}
			],
			assigned: [],
			customers: []
		};
	},
	computed: {
		isFormEmptyObject(){
			return !_.isEmpty(this.form);
		},
		assignedCustomersIds() {
			if (!_.isEmpty(this.assigned)) {
				return this.assigned.map(c => c.value);
			}
			return [];
		},
		preassignedCustomersIds() {
			let ids = [];
			if (this.form && this.form.customers) {
				ids = this.form.customers.map(c => c.customerId);
			}
			return ids;
		},
		existCustomersWithAssingedToFalse() {
			if (!_.isEmpty(this.assigned)) {
				return this.assigned.some(x => x.assigned == false);
			}
			return false;
		},
		assignedCustomersHasChanged() {
			const mapAndCompareArrays = (firstArray, secondArray) => {
				let result = secondArray.map(item => {
					const matchingFirstItem = firstArray.find(
						firstItem => firstItem.customerId === item.value
					);
					if (matchingFirstItem) {
						return {
							customerId: matchingFirstItem.customerId,
							assigned: matchingFirstItem.assigned,
							enabled: item.enabled
						};
					}
					return item;
				});

				return result;
			};
			// sort by uniqueField
			const compareObjects = (a, b) => {
				return a['customerId'].localeCompare(b['customerId']);
			};
			let assignedCustomers = _.map(this.form.customers, obj =>
				_.omit(obj, 'formsRepositoryId')
			);

			// build the new obj mapped
			let mapObjCustomers = mapAndCompareArrays(
				assignedCustomers,
				this.assigned
			);
			mapObjCustomers.sort(compareObjects);
			assignedCustomers.sort(compareObjects);

			return _.isEqual(mapObjCustomers, assignedCustomers);
		},
		disableUpdateCustomersButton() {
			let customersIds = [];
			if (this.customers) {
				customersIds = this.customers.map(c => c.customerId);
			}
			const areEquals = _.isEqual(
				customersIds,
				this.preassignedCustomersIds
			);

			return (
				areEquals &&
				!this.existCustomersWithAssingedToFalse &&
				this.assignedCustomersHasChanged
			);
		},
		customersItems() {
			return this[CUSTOMER_OPTIONS].items;
		},
		isEditPage() {
			return this.$route.path.includes('edit');
		}
	},
	methods: {
		...mapActions('forms', ['currentCustomers']),
		...mapActions('form', ['assignCustomers']),

		closeManageCustomersEditFlyout(e) {
			if (!this.validateBeforeClose) {
				this.cancelAction(e);
			}
		},
		async saveAction() {
			let customers = [];
			let newCustomers = [];

			customers.push.apply(
				customers,
				this.assigned.map(item => {
					return {
						customerId: item.value,
						assigned: true,
						enabled: item.enabled
					};
				})
			);
			let assignedCustomerIds = this.assigned.map(c => c.value);
			newCustomers = this.customers.filter(
				cust => !assignedCustomerIds.includes(cust.customerId)
			);
			customers.push.apply(
				customers,
				newCustomers.map(item => {
					return {
						customerId: item.customerId,
						assigned: false,
						enabled: item.enabled
					};
				})
			);

			let payload = [...customers];

			await this.assignCustomers({
				id: this.form.id,
				customers: payload
			}).then(isSuccessful => {
				if (isSuccessful) {
					this.cancelAction();
					this.$store.dispatch('forms/reloadForms', true);
					const updatedCustomers = customers.map(payload => {
						if (!payload.assigned) {
							return { ...payload, assigned: true };
						}
						return payload;
					});
					this.$store.dispatch(
						'forms/currentCustomers',
						updatedCustomers
					);
				}
			});
		},
		cancelAction(e) {
			if (e) e.preventDefault();
			if (!this.validateBeforeClose) {
				this.customers = [];
				this.assigned = [];
				this.$emit('show', false);
			}
		},
		updateCustomers(obj) {
			const hasValue = !!obj.value;

			const idx = this.customers.findIndex(
				x => x.customerId === obj.value
			);

			if (idx > -1) {
				if (
					hasValue &&
					!this.preassignedCustomersIds.includes(obj.value)
				) {
					Object.assign(this.customers[idx], {
						enabled: obj.enabled
					});
				} else {
					this.customers.splice(idx, 1);
				}
			} else if (hasValue) {
				this.customers.push({
					customerId: obj.value,
					enabled: obj.enabled,
					assigned: false
				});
			}
		},
		getCustomersAssigned() {
			const customers = filterAndSortArray(this.customersItems, {
				active: true
			}).map(c => {
				return Object.assign({}, c, {
					modelValue: ''
				});
			});

			let assigned = [];
			if (this.form && this.form.customers) {
				assigned = customers.filter(customer => {
					return this.form.customers
						.map(c => c.customerId)
						.includes(customer.value);
				});
			}
			//add new columns. assigned will always be true here and enabled will get updated below
			if (!_.isEmpty(assigned)) {
				assigned = assigned.map(a => ({ ...a, assigned: true }));
				//assigned = assigned.map(a => ({ ...a, enabled: false }));
			}

			if (this.form && this.form.customers) {
				if (this.form.customers.length) {
					this.form.customers.forEach(p => {
						var idx = assigned.findIndex(
							x => x.value === p.customerId
						);

						if (idx > -1) {
							Object.assign(assigned[idx], {
								enabled: p.enabled
							});
						}
					});
				}
			}

			this.assigned = assigned;
		},
		getCustomersPreassigned() {
			let preassigned = [];
			if (this.form && this.form.customers) {
				preassigned = this.form.customers.filter(item => {
					return !this.assignedCustomersIds.includes(item.value);
				});
			}

			preassigned = preassigned.map(item => {
				return {
					customerId: item.customerId,
					enabled: item.enabled
				};
			});

			this.customers = preassigned;
		},
		updateFormCustomers(customers) {
			const storedForm = this.$store.state.form.form;
			if (!_.isEmpty(storedForm)) {
				let newFormValue = _.cloneDeep(storedForm);
				Object.assign(newFormValue, { customers: customers });
				this.$store.commit('form/SetForm', newFormValue);
			}
		}
	},
	watch: {
		display: {
			immediate: true,
			handler(val) {
				if (val) {
					this.getCustomersAssigned();
					this.getCustomersPreassigned();
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
::v-deep .flyout {
	@include mq($from: md) {
		width: 675px;
	}
}

::v-deep .radio-button-group {
	width: 165px;
}

.manage-customers {
	flex-wrap: nowrap;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100%;

	&__assigned,
	&__content {
		border-radius: $form-border-radius;
		border: 1px solid $neutral-3;
		padding: $padding-md;
	}

	&__content {
		flex-grow: 1;
		flex-shrink: 1;
		min-height: 100px;
		overflow: hidden;
	}
}

.manage-customers__assigned {
	margin-bottom: $gutter-lg;

	@include mq($from: md) {
		margin-bottom: $gutter-xl;
	}

	&-title {
		display: block;
		font-weight: $medium-font-weight;
		font-size: $default-font-size;
	}

	.manage-customers__list {
		overflow: auto;
		@include scrollbar;
		padding-inline-start: 0;
		max-height: 125px;
		margin-top: $gutter-xl;

		@include mq($from: sm-l) {
			max-height: 95px;
		}
		@include mq($from: md) {
			max-height: 155px;
		}

		&-item {
			display: grid;
			gap: $gutter-lg;

			&:not(:last-child) {
				margin-bottom: $gutter-lg;
			}

			@include mq($until: md) {
				&:not(:last-child) {
					border-bottom: 1px solid $neutral-4;
				}
			}

			@include mq($from: md) {
				grid-template-columns: 1fr auto;
				gap: $gutter-xxl;
				align-items: center;
			}
		}
	}

	.radio-button-group {
		@include mq($until: md) {
			margin-bottom: $gutter-lg;
		}
	}
}
</style>
